'use client'

import React, { useState } from 'react'
import { Calendar as CalendarIcon } from 'lucide-react'
import { addDays, format } from 'date-fns'
import { DateRange } from 'react-day-picker'

import { Calendar } from '@/components/ui/calendar'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

type RangeType = 'today' | '7days' | '30days' | 'custom' | 'all'

interface DayTabProps {
  range: RangeType
  setRange: (range: RangeType, startDate?: string, endDate?: string) => void
}

const tabs: { key: RangeType; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: '7days', label: '7 Days' },
  { key: '30days', label: '30 Days' },
  { key: 'all', label: 'All Time' },
]

const DayTab = ({ range, setRange }: DayTabProps) => {
  const [activeTab, setActiveTab] = useState<RangeType>(range || 'today')

  const [dateRange, setDateRange] = useState<DateRange>({
    from: new Date(),
    to: new Date(),
  })

  const handleTabChange = (tab: RangeType) => {
    const today = new Date()
    if (tab === 'all') {
      setDateRange({ from: undefined, to: undefined })
      setActiveTab(tab)
      setRange(tab)
      return
    }

    let from: Date | undefined = undefined
    let to: Date | undefined = undefined

    if (tab === 'today') {
      from = new Date(today)
      from.setHours(0, 0, 0, 0)
      to = new Date(today)
    }

    if (tab === '7days') {
      from = addDays(today, -6)
      from.setHours(0, 0, 0, 0)
      to = new Date(today)
    }

    if (tab === '30days') {
      from = addDays(today, -29)
      from.setHours(0, 0, 0, 0)
      to = new Date(today)
    }

    // Keep date picker in sync with presets.
    if (from && to) setDateRange({ from, to })
    setActiveTab(tab)
    if (from && to) setRange(tab, from.toISOString(), to.toISOString())
  }

  const handleCustomDateChange = (selected: DateRange | undefined) => {
    const newRange = selected ?? { from: undefined, to: undefined }
    setDateRange(newRange)

    if (newRange.from && newRange.to) {
      setActiveTab('custom')
      setRange(
        'custom',
        (() => {
          const start = new Date(newRange.from!)
          start.setHours(0, 0, 0, 0)
          return start.toISOString()
        })(),
        (() => {
          const end = new Date(newRange.to!)
          end.setHours(23, 59, 59, 999)
          return end.toISOString()
        })()
      )
    }
  }

  return (
    <div className="w-full flex flex-wrap justify-between items-center gap-4">
      {/* Segmented Tabs */}
      <div className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-800 p-1 transition-colors">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={[
              "px-6 py-2 text-sm font-medium transition-colors rounded-md",
              activeTab === tab.key
                ? "bg-white dark:bg-gray-900 shadow text-gray-900 dark:text-gray-100"
                : "text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-800"
            ].join(" ")}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Custom Date Picker */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={`flex items-center gap-2 text-sm font-normal border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition ${activeTab === 'custom'
                ? 'ring-2 ring-blue-500/30 border-blue-500'
                : ''
              }`}
          >
            <span>
              {dateRange.from
                ? format(dateRange.from, 'yyyy-MM-dd')
                : 'Start'}
            </span>

            <span className="text-gray-400 dark:text-gray-500">→</span>

            <span>
              {dateRange.to
                ? format(dateRange.to, 'yyyy-MM-dd')
                : 'End'}
            </span>

            <CalendarIcon className="ml-1 h-4 w-4 text-gray-500 dark:text-gray-400" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-auto p-0 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800"
          align="end"
        >
          <Calendar
            mode="range"
            selected={dateRange}
            onSelect={handleCustomDateChange}
            numberOfMonths={2}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default DayTab
